import { NavLink, useLocation } from 'react-router-dom';
import {
  ChartLineUp,
  ShieldCheck,
  Warning,
  GearSix,
  ArrowSquareOut,
  SignOut,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { BrandMark } from '@/components/layout/BrandMark';
import { cn } from '@/lib/utils/cn';
import { useAuth } from '@/providers/AuthProvider';

interface NavItem {
  to: string;
  label: string;
  icon: Icon;
  /** Matches the active state for a quick-view query param. */
  matchView?: string;
}

const PRIMARY: NavItem[] = [
  { to: '/', label: 'Overview', icon: ChartLineUp },
  { to: '/?view=security', label: 'Security feed', icon: ShieldCheck, matchView: 'security' },
  { to: '/?view=breaking', label: 'Breaking changes', icon: Warning, matchView: 'breaking' },
];

function NavRow({ item }: { item: NavItem }) {
  const location = useLocation();
  const currentView = new URLSearchParams(location.search).get('view');
  const isOverview = item.to === '/' && location.pathname === '/' && !currentView;
  const isView = item.matchView != null && location.pathname === '/' && currentView === item.matchView;
  const active = isOverview || isView;
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      className={cn(
        'group relative flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-md text-[13.5px] transition-colors',
        active
          ? 'text-brand bg-brand/[0.08] font-semibold'
          : 'text-muted font-medium hover:text-ink hover:bg-panel-2',
      )}
    >
      <span
        className={cn(
          'absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full transition-opacity',
          active ? 'bg-brand opacity-100' : 'opacity-0',
        )}
        aria-hidden
      />
      <Icon size={18} weight={active ? 'fill' : 'regular'} aria-hidden />
      {item.label}
    </NavLink>
  );
}

function initials(name?: string, email?: string): string {
  const src = name?.trim() || email?.split('@')[0] || '?';
  const parts = src.split(/[.\s_-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

function AccountCard() {
  const { user, logout } = useAuth();
  if (!user) return null;
  const label = user.name || user.given_name || user.email || 'Signed in';

  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="flex items-center gap-3 rounded-md px-2 py-1.5">
        <span className="grid place-items-center w-9 h-9 shrink-0 rounded-md bg-brand text-white text-[12px] font-bold">
          {initials(user.name, user.email)}
        </span>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[13px] font-semibold text-ink">{label}</div>
          {user.email ? (
            <div className="truncate text-[11px] text-muted-2">{user.email}</div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={logout}
          aria-label="Sign out"
          title="Sign out"
          className="ml-auto grid place-items-center w-8 h-8 shrink-0 rounded-md text-muted hover:text-danger hover:bg-panel-2 transition-colors"
        >
          <SignOut size={17} aria-hidden />
        </button>
      </div>
    </div>
  );
}

export function Sidebar() {
  return (
    <div className="flex h-full flex-col gap-7 px-4 py-5">
      <NavLink to="/" className="flex items-center gap-3 px-1">
        <BrandMark size={36} />
        <div className="leading-tight">
          <div className="font-semibold text-[15px] tracking-tight text-ink">
            Release Observatory
          </div>
          <div className="text-[11px] font-medium tracking-wide text-muted">
            Trimble Construction
          </div>
        </div>
      </NavLink>

      <nav className="flex flex-col gap-1">
        <p className="px-4 pb-1.5 text-[11px] font-semibold tracking-wide uppercase text-muted-2">
          Views
        </p>
        {PRIMARY.map((item) => (
          <NavRow key={item.label} item={item} />
        ))}
      </nav>

      <nav className="flex flex-col gap-1 mt-auto">
        <p className="px-4 pb-1.5 text-[11px] font-semibold tracking-wide uppercase text-muted-2">
          System
        </p>
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              'relative flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-md text-[13.5px] transition-colors',
              isActive
                ? 'text-brand bg-brand/[0.08] font-semibold'
                : 'text-muted font-medium hover:text-ink hover:bg-panel-2',
            )
          }
        >
          <GearSix size={18} aria-hidden />
          Connection
        </NavLink>
        <a
          href="https://help.trimble.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-md text-[13.5px] font-medium text-muted hover:text-ink hover:bg-panel-2 transition-colors"
        >
          <ArrowSquareOut size={18} aria-hidden />
          Help center
        </a>
      </nav>

      <AccountCard />
    </div>
  );
}
